// Cache for Patients to avoid redundant API calls
export class PatientCache {
  constructor(ttlMinutes = 30) {
    this.cache = new Map();
    this.ttlMs = ttlMinutes * 60 * 1000;
    this.hits = 0;
    this.misses = 0;
    console.log("🔄 PatientCache initialized");
  }

  get(id) {
    if (!this.cache.has(id)) {
      this.misses++;
      return null;
    }

    const entry = this.cache.get(id);
    // Check if entry has expired
    if (Date.now() > entry.expiry) {
      this.cache.delete(id);
      this.misses++;
      return null;
    }

    this.hits++;
    return entry.data;
  }

  set(id, data) {
    const expiry = Date.now() + this.ttlMs;
    this.cache.set(id, { data, expiry });
    return this;
  }

  has(id) {
    if (!this.cache.has(id)) {
      return false;
    }

    const entry = this.cache.get(id);
    if (Date.now() > entry.expiry) {
      this.cache.delete(id);
      return false;
    }

    return true;
  }

  // Utility method to log cache statistics and contents
  logStats() {
    // Log sample of cache contents (first 5 entries)
    let count = 0;
    for (const [id, entry] of this.cache.entries()) {
      if (count < 5) {
        const patient = entry.data;
        count++;
      } else {
        break;
      }
    }
  }

  // Enhanced storage methods with logging
  saveToStorage() {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        // Only store the necessary data (id, patient data, expiry)
        const simplified = Array.from(this.cache.entries()).map(
          ([id, entry]) => {
            return [
              id,
              {
                data: entry.data,
                expiry: entry.expiry,
              },
            ];
          },
        );

        const serialized = JSON.stringify(simplified);
        localStorage.setItem("patientCache", serialized);
      }
    } catch (error) {
      console.error("❌ Error saving cache to storage:", error);
    }
  }

  loadFromStorage() {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const serialized = localStorage.getItem("patientCache");
        if (serialized) {
          const entries = JSON.parse(serialized);

          // Clear existing cache
          this.cache.clear();

          // Track loaded and expired entries
          let loadedCount = 0;
          let expiredCount = 0;

          // Load entries
          for (const [id, entry] of entries) {
            if (Date.now() <= entry.expiry) {
              this.cache.set(id, entry);
              loadedCount++;
            } else {
              expiredCount++;
            }
          }
        } else {
          console.log("📂 No cache found in storage");
        }
      }
    } catch (error) {
      console.error("❌ Error loading cache from storage:", error);
    }
  }

  // Add method to inspect cache for a specific patient ID
  inspect(id) {
    if (!this.cache.has(id)) {
      return;
    }

    const entry = this.cache.get(id);
    const patient = entry.data;
    console.log(`   Found in cache:`);
    console.log(`   Name: ${patient.fields["Name"] || "Unknown"}`);
    console.log(`   First Name: ${patient.fields["First Name"] || "Unknown"}`);
    console.log(`   Last Name: ${patient.fields["Last Name"] || "Unknown"}`);
    console.log(`   Record ID: ${patient.fields["Record ID"] || "Unknown"}`);
    console.log(`   Expires: ${new Date(entry.expiry).toLocaleTimeString()}`);
    console.log(`   Is Expired: ${Date.now() > entry.expiry ? "Yes" : "No"}`);
  }

  // Clear the cache
  clear() {
    const size = this.cache.size;
    this.cache.clear();
  }
}
